import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMsg, colones } from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";

export function useCotizacion(id) {
  const navigate = useNavigate();
  const [cot, setCot] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/cotizaciones/${id}`);
      setCot(data);
    } catch (e) { toast.error(errorMsg(e)); navigate("/"); }
  }, [id, navigate]);

  useEffect(() => { cargar(); }, [cargar]);

  const mutar = async (fn) => {
    try {
      const { data } = await fn();
      setCot(data);
      if (data.sugerencia_operacion) {
        toast.info(`Sugerencia: este repuesto pertenece a la operación "${data.sugerencia_operacion.nombre}". Cargue la operación combinada para no duplicar el desarme.`, { duration: 8000 });
      }
      return true;
    } catch (e) { toast.error(errorMsg(e)); return false; }
  };

  const agregarRepuesto = (rep) =>
    mutar(() => api.post(`/cotizaciones/${id}/items`, { repuesto_id: rep.id, cantidad: 1 }));

  const cambiarCantidad = (item, delta) => {
    const nueva = item.cantidad + delta;
    if (nueva < 1) return Promise.resolve(false);
    return mutar(() => api.patch(`/cotizaciones/${id}/items/${item.id}`, { cantidad: nueva }));
  };

  const eliminarItem = (itemId) => mutar(() => api.delete(`/cotizaciones/${id}/items/${itemId}`));

  const sustituirItem = (itemId, nuevoRepuestoId) =>
    mutar(() => api.post(`/cotizaciones/${id}/items/${itemId}/sustituir?nuevo_repuesto_id=${nuevoRepuestoId}`));

  const cargarPlantilla = async (pid, confirmar = false) => {
    try {
      const { data } = await api.post(`/cotizaciones/${id}/cargar-plantilla?plantilla_id=${pid}${confirmar ? "&confirmar=true" : ""}`);
      setCot(data);
      toast.success("Plantilla cargada");
      return true;
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(errorMsg(e))) return cargarPlantilla(pid, true);
        return false;
      }
      toast.error(errorMsg(e));
      return false;
    }
  };

  const guardarManoObra = (linea) => {
    const payload = { descripcion: linea.descripcion, monto: Number(linea.monto) || 0 };
    return mutar(() => linea.id
      ? api.patch(`/cotizaciones/${id}/mano-obra/${linea.id}`, payload)
      : api.post(`/cotizaciones/${id}/mano-obra`, payload));
  };

  const eliminarManoObra = (moId) => mutar(() => api.delete(`/cotizaciones/${id}/mano-obra/${moId}`));

  const aplicarAccionesIA = async (acciones) => {
    for (const pid of acciones.cargar_plantillas || []) {
      await cargarPlantilla(pid);
    }
    for (const it of acciones.agregar_items || []) {
      await api.post(`/cotizaciones/${id}/items`, { repuesto_id: it.repuesto_id, cantidad: it.cantidad || 1 });
    }
    await cargar();
  };

  const exportarPdf = async () => {
    const r = await api.get(`/cotizaciones/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `cotizacion-${cot.nombre}.pdf`; a.click();
  };

  const copiarLista = async () => {
    const lineas = [
      `COTIZACIÓN: ${cot.nombre}`, "",
      "REPUESTOS:",
      ...cot.items.map((it) => `${it.codigo_parte}\t${it.nombre}\tx${it.cantidad}`),
      "", "MANO DE OBRA:",
      ...cot.mano_obra.map((m) => `${m.descripcion}\t${colones(m.monto)}`),
      `TOTAL MANO DE OBRA: ${colones(cot.total_mano_obra)}`,
    ];
    try {
      await navigator.clipboard.writeText(lineas.join("\n"));
      toast.success("Lista copiada al portapapeles");
    } catch (error) {
      logError("Error al copiar al portapapeles:", error);
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  return { cot, cargar, agregarRepuesto, cambiarCantidad, eliminarItem, sustituirItem, cargarPlantilla,
    guardarManoObra, eliminarManoObra, aplicarAccionesIA, exportarPdf, copiarLista };
}
